import { useCallback, useEffect, useMemo, useState } from "react";
import isEmpty from "lodash/isEmpty";
import { useDispatch, useSelector } from "react-redux";
import { useNavigate } from "react-router";
import { useLocalStorage } from "usehooks-ts";

import { KIOSK_TIME, STORAGE_KEYS } from "@constants/Kiosk";
import { kioskRouteEnum } from "@enums/route.enum";
import { useBooking } from "@hooks/useBooking/useBooking";
import { useKioskIdleTimer } from "@hooks/useKioskIdleTimer";
import { landingPageKeys, useLandingPageData } from "@hooks/useLandingPageData";
import { usePricing } from "@hooks/usePricing/usePricing";
import { ResourceInfoType } from "@models/components/units.model";
import { BookingFetchResponseType } from "@models/services/booking";
import { getLocationType, locationType } from "@models/services/organisation";
import { userProfile } from "@models/services/user";
import handleResponse from "@services/apiConfig/api.response.service";
import API_SERVICES from "@services/index";
import bookingSlice from "@slices/booking.slice";
import drawerSlice from "@slices/drawer.slice";
import kioskSlice from "@slices/kiosk.slice";
import localSlice from "@slices/local.slice";
import { RootState } from "@store/index";
import { useQueryClient } from "@tanstack/react-query";
import commonUtils from "@utils/common.utils";

export const useKioskBooking = () => {
  const [deviceId] = useLocalStorage(STORAGE_KEYS.KIOSK_DEVICE_ID, "");
  const { data: landingPageData, refetch: refetchLandingPageData } =
    useLandingPageData(deviceId);

  const [bookingLoader, setBookingLoader] = useState(true);
  const [bookingSlots, setBookingSlots] = useState<BookingFetchResponseType>(
    {} as BookingFetchResponseType
  );
  const [locationDetails, setLocationDetails] = useState<getLocationType>(
    {} as getLocationType
  );
  const [activeLocation, setActiveLocation] = useState<
    locationType | ResourceInfoType
  >({} as locationType);
  const [userProfile, setUserProfile] = useState<userProfile>();

  const accessToken = useSelector(
    (State: RootState) => State.local.accessToken
  );
  const isAuthenticated = useSelector(
    (State: RootState) => State.local.isAuthenticated
  );
  const paymentLoader = useSelector(
    (State: RootState) => State.booking.paymentLoader
  );
  const selectedDate = useSelector(
    (State: RootState) => State.booking.selectedDate
  );
  const selectedTime = useSelector(
    (State: RootState) => State.booking.selectedTime
  );
  const guestList = useSelector((State: RootState) => State.booking.guestList);
  const kioskBookingJustSaved = useSelector(
    (State: RootState) => State.kiosk.kioskBookingJustSaved
  );
  const bookingSuccessDetails = useSelector(
    (State: RootState) => State.kiosk.bookingSuccessDetails
  );

  const dispatch = useDispatch();
  const navigate = useNavigate();
  const queryClient = useQueryClient();

  const {
    fetchQuotaInfo,
    fetchResourcesPrices,
    priceLoader,
    syncQuotaWithBookingSlots,
  } = usePricing();
  const { buildTimeSlot } = useBooking();

  const { remainingTime, reset, showWarning } = useKioskIdleTimer({
    onTimeout: () => {
      hideSuccessModal(true, true);
      dispatch(localSlice.actions.clearStorage(null));
      dispatch(bookingSlice.actions.flush(null));
      localStorage.removeItem("persist:root");
      sessionStorage.removeItem(STORAGE_KEYS.LANDING_REDIRECTED);
    },
    enabled:
      isAuthenticated ||
      Boolean(landingPageData?.clublogoEnabled) ||
      Boolean(landingPageData?.sponsorEnabled),
    redirectTo: kioskRouteEnum.Landing,
    timeout: KIOSK_TIME.IDLE_TIMEOUT,
    warningTime: KIOSK_TIME.WARNING_TIME,
  });

  const getOrganisationInfo = useCallback(async () => {
    const data = await API_SERVICES.KIOSK.ORGANISATION_INFO();
    const responseData = handleResponse(data, navigate);
    if (responseData?.ok) {
      setLocationDetails(responseData.data);
      if (responseData?.data?.locations.length && isEmpty(activeLocation)) {
        setActiveLocation(responseData?.data?.locations[0]);
      }
    }
  }, [activeLocation]);

  const getBookingSlots = useCallback(async () => {
    setBookingLoader(true);
    dispatch(kioskSlice.actions.setIsSlotsLoading(true));

    const data = await API_SERVICES.KIOSK.GET_BOOKING_SLOT({
      bookingDate: selectedDate,
      isAuthenticated,
      locationId: activeLocation?.id,
    });
    const responseData = handleResponse(data);
    if (responseData?.ok) {
      setBookingSlots(responseData.data);
      dispatch(
        bookingSlice.actions.setBackToBackBookingEnabled(
          responseData.data?.backToBackBookingEnabled ?? false
        )
      );
      setBookingLoader(false);
    }
    dispatch(kioskSlice.actions.setIsSlotsLoading(false));
  }, [activeLocation?.id, selectedDate, isAuthenticated, dispatch]);

  const getUserProfile = useCallback(async () => {
    const data = await API_SERVICES.USER.PROFILE();
    const responseData = handleResponse(data);
    if (responseData?.ok) {
      setUserProfile(responseData.data);
    }
  }, []);

  const refetchAll = useCallback(async () => {
    await getOrganisationInfo();
    if (activeLocation?.id) await getBookingSlots();
    if (accessToken !== "") await getUserProfile();
  }, [
    getOrganisationInfo,
    getBookingSlots,
    getUserProfile,
    activeLocation?.id,
    accessToken,
  ]);

  useEffect(() => {
    getOrganisationInfo();
  }, [isAuthenticated]);

  useEffect(() => {
    if (!activeLocation?.id) return;
    getBookingSlots();
  }, [activeLocation?.id, selectedDate, getBookingSlots, isAuthenticated]);

  useEffect(() => {
    if (accessToken !== "") getUserProfile();
  }, [accessToken]);

  useEffect(() => {
    buildTimeSlot(bookingSlots);
  }, [bookingSlots]);

  useEffect(() => {
    if (!selectedTime || !bookingSlots?.resources?.length) return;
    fetchResourcesPrices(bookingSlots, false);
  }, [selectedTime, guestList, bookingSlots?.resources?.length]);

  useEffect(() => {
    if (!kioskBookingJustSaved) return;
    (async () => {
      await refetchAll();
      dispatch(kioskSlice.actions.setKioskBookingJustSaved(false));
    })();
  }, [kioskBookingJustSaved, refetchAll, dispatch]);

  const handleContinueSession = () => {
    reset();
  };

  const handleLocationChange = (tab: locationType | ResourceInfoType) => {
    setActiveLocation(tab);
    dispatch(bookingSlice.actions.resetBookingData(null));
    dispatch(drawerSlice.actions.removeDrawerBookingState(null));
  };

  const hideSuccessModal = async (redirect = true, logOut = false) => {
    dispatch(
      kioskSlice.actions.setBookingSuccessDetails({
        bookingId: "",
        showModal: false,
      })
    );

    if (logOut) {
      commonUtils.clearUserSession(false);
    }

    let currentLandingData = landingPageData;

    if (redirect) {
      const result = await refetchLandingPageData();

      if (result?.data) {
        currentLandingData = result.data;
      } else if (result?.error) {
        queryClient.invalidateQueries({ queryKey: landingPageKeys.all });
      }
    } else {
      queryClient.invalidateQueries({ queryKey: landingPageKeys.all });
    }

    if (
      redirect &&
      (currentLandingData?.clublogoEnabled ||
        currentLandingData?.sponsorEnabled)
    ) {
      navigate(kioskRouteEnum.Landing);
    }
  };

  useEffect(() => {
    dispatch(bookingSlice.actions.flush(null));
    commonUtils.clearUserSession(false);
    hideSuccessModal(false);
  }, []);

  const quoteInfoPayload = useMemo(() => {
    if (!selectedTime || !bookingSlots?.resources?.length) return null;

    return {
      bookingDate: selectedDate,
      locationId: bookingSlots?.locationId,
      selectedTime,
    };
  }, [selectedTime, selectedDate, bookingSlots?.locationId]);

  useEffect(() => {
    if (!quoteInfoPayload) return;
    const loadQuotaInfo = async () => {
      const quotaData = await fetchQuotaInfo(quoteInfoPayload);

      if (quotaData) {
        setBookingSlots(prevBookingSlots =>
          syncQuotaWithBookingSlots(quotaData, prevBookingSlots)
        );
      }
    };

    loadQuotaInfo();
  }, [quoteInfoPayload]);

  return {
    activeLocation,
    bookingLoader,
    bookingSlots,
    bookingSuccessDetails,
    getBookingSlots,
    handleContinueSession,
    handleLocationChange,
    hideSuccessModal,
    locationDetails,
    paymentLoader,
    priceLoader,
    remainingTime,
    showWarning,
    userProfile,
  };
};
